// Nearby on the restaurant page (#368/#369). The backend decides where to
// measure from and whether anything is "near"; this file only turns its answer
// into what the page says. No distance logic belongs here.

const LOCATION_KEY = 'dynerz_user_location';
const LOCATION_MAX_AGE_MS = 60 * 60 * 1000; // same 1h window as the restaurant list

// The viewer's position from the restaurant list's cache, if it is fresh.
export function cachedViewerLocation(storage = window.localStorage) {
  try {
    const saved = JSON.parse(storage.getItem(LOCATION_KEY));
    if (saved && Date.now() - saved.timestamp < LOCATION_MAX_AGE_MS
        && Number.isFinite(saved.lat) && Number.isFinite(saved.lng)) {
      return { lat: saved.lat, lng: saved.lng };
    }
  } catch (e) {
    // unreadable or blocked storage: measure without the viewer
  }
  return null;
}

// The viewer's position for Nearby (#369): the cached one, else the browser's,
// but only when location permission is ALREADY granted. The detail page never
// prompts: a permission dialog on every restaurant page is a worse trade than
// measuring from the restaurant. Relying on the cache alone missed most visits,
// because the list writes it only when Distance sort first gets a fix.
export async function viewerLocation(nav = window.navigator, storage = window.localStorage) {
  const cached = cachedViewerLocation(storage);
  if (cached) return cached;
  try {
    if (!nav.permissions || !nav.geolocation) return null;
    const status = await nav.permissions.query({ name: 'geolocation' });
    if (status.state !== 'granted') return null;
    const position = await new Promise((resolve) => {
      nav.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { timeout: 5000, maximumAge: 5 * 60 * 1000 }
      );
    });
    if (position) {
      try {
        storage.setItem(LOCATION_KEY, JSON.stringify({ ...position, timestamp: Date.now() }));
      } catch (e) {
        // storage blocked: still use the position for this request
      }
    }
    return position;
  } catch (e) {
    return null; // no Permissions API for geolocation (older browsers): measure without
  }
}

export function nearbyUrl(apiBaseUrl, restaurantId, viewer) {
  const params = new URLSearchParams({ limit: '8' });
  if (viewer) {
    params.set('viewer_latitude', String(viewer.lat));
    params.set('viewer_longitude', String(viewer.lng));
  }
  return `${apiBaseUrl}/restaurants/${restaurantId}/nearby?${params}`;
}

// The response's groups. A backend without #369 sends only the flat list, so
// wrap it as one within-radius group rather than show nothing.
export function nearbyGroups(data) {
  if (Array.isArray(data?.groups)) {
    return data.groups.filter((g) => (g.nearby_restaurants || []).length > 0);
  }
  const rows = data?.nearby_restaurants || [];
  return rows.length ? [{ anchor: null, within_radius: true, nearby_restaurants: rows }] : [];
}

function anchorName(anchor) {
  if (!anchor) return null;
  return [anchor.address, anchor.city].map((part) => (part || '').trim()).filter(Boolean).join(', ') || null;
}

// The line above a group's cards. Null when the cards speak for themselves:
// one group, and everything in it is within the radius.
export function groupHeading(group, { grouped, radius }) {
  const near = grouped ? anchorName(group.anchor) : null;
  if (!group.within_radius) {
    const where = near ? ` of ${near}` : '';
    return `Nothing on dyner.z within ${radius} mi${where}. The nearest places we know:`;
  }
  return near ? `Near ${near}` : null;
}
